import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@vkontakte/vkui';
import { confirmCode, resendCode } from '../../utils/authUtils';
import logo from '../../assets/images/logo_512.png';
import './CodeStep.css';

const onlyDigits = (value) => (value || '').replace(/\D/g, '');

const formatSeconds = (total) => {
  const safe = Math.max(0, total);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Сколько секунд осталось до срока. Считаем от самого срока, а не вычитаем по
 * секунде: в фоновой вкладке браузер замедляет таймеры, и счёт отставал бы.
 * setter задаёт новый срок — через столько-то секунд от текущего момента.
 */
const useCountdown = (initial) => {
  const [deadline, setDeadline] = useState(() => Date.now() + (initial || 0) * 1000);
  const remaining = () => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const [left, setLeft] = useState(remaining);
  useEffect(() => {
    setLeft(remaining());
    const timer = setInterval(() => setLeft(remaining()), 500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  const reset = (seconds) => setDeadline(Date.now() + (seconds || 0) * 1000);
  return [left, reset];
};

// Ссылки под кнопкой — как «Зарегистрироваться» и «Вернуться к входу» на форме входа.
const linkStyle = {
  background: 'none',
  border: 'none',
  color: '#6c757d',
  fontSize: '14px',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
};

/**
 * Второй шаг входа или регистрации: код из письма. Оформлен как сама форма
 * входа (LoginPanel): те же карточка, логотип, заголовок, кнопка и ссылки.
 *
 * challenge — ответ сервера (challenge_id, email, code_length, resend_in,
 * expires_in). email — почта, которую человек ввёл сам: показываем её целиком,
 * сервер присылает её скрытой. Код можно вставить целиком: он разложится по
 * ячейкам. Когда введены все цифры, код отправляется сам.
 */
const CodeStep = ({ purpose, email, challenge: initialChallenge, onSuccess, onBack }) => {
  const [challenge, setChallenge] = useState(initialChallenge);
  const length = challenge.code_length || 6;
  const [digits, setDigits] = useState(() => Array(length).fill(''));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendIn, setResendIn] = useCountdown(challenge.resend_in);
  const [expiresIn, setExpiresIn] = useCountdown(challenge.expires_in || 3600);
  const [expired, setExpired] = useState(false);
  const inputs = useRef([]);

  const isRegister = purpose === 'register';
  const isReset = purpose === 'reset';

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (expiresIn <= 0 && !expired) {
      setExpired(true);
      setNotice('');
      setError('Срок действия кода истёк. Начните заново, чтобы получить новый.');
    }
  }, [expiresIn, expired]);

  const focusCell = (index) => {
    const cell = inputs.current[Math.max(0, Math.min(length - 1, index))];
    if (cell) {
      cell.focus();
      cell.select();
    }
  };

  const submit = async (code) => {
    if (isChecking || expired) return;
    setError('');
    setNotice('');
    setIsChecking(true);
    const result = await confirmCode(purpose, challenge.challenge_id, code);
    setIsChecking(false);
    if (result.success) {
      onSuccess(result.data);
      return;
    }
    setError(result.error);
    setDigits(Array(length).fill(''));
    if (result.status === 410) {
      setExpired(true);
    } else {
      setTimeout(() => focusCell(0), 0);
    }
  };

  const fill = (index, text) => {
    // Код целиком (вставка или автоподстановка) раскладываем с первой ячейки,
    // в какую бы ячейку его ни вставили.
    const all = onlyDigits(text);
    const startIndex = all.length >= length ? 0 : index;
    const incoming = all.slice(0, length - startIndex);
    if (!incoming) return;
    // Начал вводить заново — прежняя ошибка уже не про этот код.
    if (error) setError('');
    const next = [...digits];
    incoming.split('').forEach((digit, offset) => {
      next[startIndex + offset] = digit;
    });
    setDigits(next);
    const code = next.join('');
    if (code.length === length && !next.includes('')) {
      submit(code);
    } else {
      focusCell(startIndex + incoming.length);
    }
  };

  const handleChange = (index, value) => {
    const clean = onlyDigits(value);
    if (!clean) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }
    // Весь код сразу — это автоподстановка из письма (one-time-code); иначе
    // в ячейку попадает последняя набранная цифра.
    fill(index, clean.length >= length ? clean : clean.slice(-1));
  };

  const handleKeyDown = (index, event) => {
    // Цифру с клавиатуры ставим сами: если в ячейке уже та же цифра, браузер не
    // присылает onChange, и курсор не перешёл бы к следующей ячейке.
    if (/^\d$/.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      fill(index, event.key);
      return;
    }
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      focusCell(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    fill(index, event.clipboardData.getData('text'));
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setIsResending(true);
    const result = await resendCode(challenge.challenge_id);
    setIsResending(false);
    if (result.success) {
      const next = result.challenge;
      setChallenge(next);
      setResendIn(next.resend_in || 60);
      setExpiresIn(next.expires_in || 3600);
      setDigits(Array(length).fill(''));
      setNotice('Новый код отправлен. Прежний больше не действует.');
      setTimeout(() => focusCell(0), 0);
      return;
    }
    if (result.resendIn) setResendIn(result.resendIn);
    if (result.status === 410) setExpired(true);
    setError(result.error);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const code = digits.join('');
    if (code.length < length) {
      setError(`Введите все ${length} цифр кода`);
      return;
    }
    submit(code);
  };

  return (
    <>
      <div className="login-panel-header">
        <div className="login-panel-logo-container">
          <img src={logo} alt="Логотип" className="login-panel-logo" />
        </div>
        <h2 className="login-panel-title">Проверьте почту</h2>
        <p className="login-panel-subtitle">
          {isRegister && 'Код для подтверждения регистрации отправлен на'}
          {isReset && 'Код для сброса пароля отправлен на'}
          {!isRegister && !isReset && 'Код для входа отправлен на'}
          <br />
          <b className="code-step-email">{email || challenge.email}</b>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="login-panel-form" noValidate>
        <div className="login-panel-field">
          <label htmlFor="code-cell-0" className="login-panel-label">
            Код из письма
          </label>
          <div className="code-step-cells" role="group" aria-label="Код из письма">
            {digits.map((digit, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                id={`code-cell-${index}`}
                ref={(el) => { inputs.current[index] = el; }}
                className={`code-step-cell${digit ? ' is-filled' : ''}${error && !notice ? ' is-error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={length}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(index, e)}
                onFocus={(e) => e.target.select()}
                disabled={isChecking || expired}
                aria-label={`Цифра ${index + 1}`}
              />
            ))}
          </div>
          {!expired && (
            <div className="code-step-timer">Код действует ещё {formatSeconds(expiresIn)}</div>
          )}
        </div>

        {notice && <div className="login-panel-error code-step-notice">{notice}</div>}
        {error && <div className="login-panel-error">{error}</div>}

        <Button
          type={expired ? 'button' : 'submit'}
          size="l"
          mode="outline"
          appearance="neutral"
          disabled={!expired && isChecking}
          className="login-panel-button"
          style={{ width: '100%' }}
          onClick={expired ? onBack : undefined}
        >
          {expired ? 'Начать заново' : isChecking ? 'Проверка...' : 'Подтвердить'}
        </Button>

        <div className="code-step-links">
          <button type="button" onClick={onBack} style={linkStyle}>
            {isRegister && 'Вернуться к регистрации'}
            {isReset && 'Указать другую почту'}
            {!isRegister && !isReset && 'Войти с другой почтой'}
          </button>

          {!expired && (resendIn > 0 ? (
            <span className="code-step-wait">Отправить код ещё раз можно через {formatSeconds(resendIn)}</span>
          ) : (
            <button type="button" onClick={handleResend} disabled={isResending} style={linkStyle}>
              {isResending ? 'Отправка...' : 'Отправить код ещё раз'}
            </button>
          ))}
        </div>
      </form>
    </>
  );
};

export default CodeStep;
